import PropTypes from 'prop-types'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useReducer,
} from 'react'
import {
  getProductSearchResults,
  getSearchIndexDocumentIds,
} from './productSearch'
import {
  createEmptyLocalSearchIndex,
  loadLocalSearchIndex,
  preloadLocalSearchIndex,
} from './localSearchIndex'
import { useAuth } from '../../utils/context/AuthContext'
import {
  filterCatalogSearchResults,
  getSearchResultAccessState,
  getUnrequestedPageToken,
  isSearchQueryEligible,
  matchesSearchQuery,
  mergeSearchResultPages,
  normalizeSearchQuery,
  normalizeSearchResults,
  rankSearchResults,
} from './searchUtils'

const DEFAULT_SEARCH_ENDPOINT =
  'https://us-central1-harborcapital-se-1627069964559.cloudfunctions.net/vertex'

const createEmptySearchResponse = () => ({
  results: [],
  retrievedCount: 0,
  totalSize: 0,
  nextPageToken: null,
  attributionToken: null,
  summary: null,
  queryExpansionInfo: null,
  semanticState: null,
  hasAccessFilteredResults: false,
  hasCatalogFilteredResults: false,
})

const getCanonicalProductPath = (pathKey, availablePagePaths) => {
  if (
    !availablePagePaths ||
    typeof pathKey !== 'string' ||
    !pathKey.endsWith('/welcome')
  ) {
    return null
  }

  const productPathMatch = pathKey.match(
    /^\/(?:(fund|etf)\/)?([^/]+)\/welcome$/
  )
  if (!productPathMatch) return null

  const [, productType, ticker] = productPathMatch
  const candidates = productType
    ? [`/${productType}/${ticker}`]
    : [`/fund/${ticker}`, `/etf/${ticker}`]
  const matches = candidates.filter((candidate) =>
    availablePagePaths.has(candidate)
  )

  return matches.length === 1 ? matches[0] : null
}

const getPortfolioSearchResults = (
  searchTerms,
  portfolioIndex,
  canViewModelDetails = false
) => {
  if (!isSearchQueryEligible(searchTerms)) return []

  const documents = Array.isArray(portfolioIndex?.documents)
    ? portfolioIndex.documents
    : []

  return getSearchIndexDocumentIds(searchTerms, portfolioIndex?.prefixes)
    .map((documentIndex) => documents[documentIndex])
    .filter(Boolean)
    .filter(
      (result) => result.accessState !== 'institutional' || canViewModelDetails
    )
    .filter((result) =>
      matchesSearchQuery(result.document?.derivedStructData?.title, searchTerms)
    )
}

const initialSearchState = {
  results: [],
  retrievedCount: 0,
  status: 'idle',
  searchTerms: '',
  error: null,
  loadMoreError: null,
  totalSize: 0,
  nextPageToken: null,
  attributionToken: null,
  summary: null,
  queryExpansionInfo: null,
  semanticState: null,
  hasAccessFilteredResults: false,
  hasCatalogFilteredResults: false,
}

const SearchContext = createContext({
  ...initialSearchState,
  preloadSearchIndex: () => {},
  search: () => {},
})

const searchReducer = (state, action) => {
  switch (action.type) {
    case 'search::start':
      return {
        ...state,
        ...(action.payload.append
          ? {}
          : { results: [], totalSize: 0, nextPageToken: null }),
        status: action.payload.append ? 'loading-more' : 'loading',
        searchTerms: action.payload.searchTerms,
        error: null,
        loadMoreError: null,
      }
    case 'search::finish': {
      const results = action.append
        ? mergeSearchResultPages(state.results, action.payload.results)
        : action.payload.results
      const hasAccessFilteredResults = action.append
        ? Boolean(
            state.hasAccessFilteredResults ||
            action.payload.hasAccessFilteredResults
          )
        : Boolean(action.payload.hasAccessFilteredResults)
      const hasCatalogFilteredResults = action.append
        ? Boolean(
            state.hasCatalogFilteredResults ||
            action.payload.hasCatalogFilteredResults
          )
        : Boolean(action.payload.hasCatalogFilteredResults)
      return {
        ...state,
        ...action.payload,
        hasAccessFilteredResults,
        hasCatalogFilteredResults,
        results,
        retrievedCount: results.length,
        totalSize:
          hasAccessFilteredResults || hasCatalogFilteredResults
            ? results.length
            : action.payload.totalSize,
        status: 'idle',
        error: null,
        loadMoreError: null,
      }
    }
    case 'search::error':
      return action.payload.append
        ? {
            ...state,
            status: 'idle',
            error: null,
            loadMoreError: action.payload.message,
          }
        : {
            ...state,
            status: 'idle',
            error: action.payload.message,
            loadMoreError: null,
          }
    default:
      return state
  }
}

const SearchProvider = ({ searchFn, preloadSearchIndex, children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialSearchState)
  const activeRequestRef = useRef(null)
  const requestSequenceRef = useRef(0)
  const requestedPageTokensRef = useRef(new Set())

  const createRequest = useCallback(() => {
    const controller = new AbortController()
    let timedOut = false
    let timeoutId = null
    let timeoutSignal = null
    let handleTimeout = null

    if (
      typeof AbortSignal !== 'undefined' &&
      typeof AbortSignal.timeout === 'function'
    ) {
      timeoutSignal = AbortSignal.timeout(10000)
      handleTimeout = () => {
        timedOut = true
        if (!controller.signal.aborted) controller.abort()
      }
      timeoutSignal.addEventListener('abort', handleTimeout, { once: true })
    } else {
      timeoutId = setTimeout(() => {
        timedOut = true
        controller.abort()
      }, 10000)
    }

    const signal =
      timeoutSignal && typeof AbortSignal.any === 'function'
        ? AbortSignal.any([controller.signal, timeoutSignal])
        : controller.signal

    return {
      controller,
      signal,
      didTimeout: () => timedOut,
      cleanup: () => {
        if (timeoutId) clearTimeout(timeoutId)
        if (timeoutSignal && handleTimeout) {
          timeoutSignal.removeEventListener('abort', handleTimeout)
        }
      },
    }
  }, [])

  const search = useCallback(
    async (searchTerms, { append = false, pageToken = null } = {}) => {
      const normalizedSearchTerms = normalizeSearchQuery(searchTerms)
      if (
        append &&
        (!pageToken || requestedPageTokensRef.current.has(pageToken))
      ) {
        return
      }

      const sequence = ++requestSequenceRef.current
      const activeRequest = activeRequestRef.current
      activeRequest?.controller.abort()
      activeRequest?.cleanup()
      activeRequestRef.current = null

      if (!isSearchQueryEligible(normalizedSearchTerms)) {
        requestedPageTokensRef.current.clear()
        dispatch({
          type: 'search::finish',
          payload: {
            ...createEmptySearchResponse(),
            searchTerms: normalizedSearchTerms,
          },
        })
        return
      }

      if (!append) requestedPageTokensRef.current.clear()
      if (append) requestedPageTokensRef.current.add(pageToken)

      dispatch({
        type: 'search::start',
        payload: { append, searchTerms: normalizedSearchTerms },
      })

      const request = createRequest()
      activeRequestRef.current = request

      try {
        const response = await searchFn(normalizedSearchTerms, request.signal, {
          pageToken,
        })

        if (sequence !== requestSequenceRef.current) return

        const responsePayload = Array.isArray(response)
          ? { ...createEmptySearchResponse(), results: response }
          : response

        dispatch({
          type: 'search::finish',
          payload: {
            ...responsePayload,
            nextPageToken: getUnrequestedPageToken(
              responsePayload.nextPageToken,
              requestedPageTokensRef.current
            ),
          },
          append,
        })
      } catch (error) {
        if (sequence !== requestSequenceRef.current) return
        if (error?.name === 'AbortError' && !request.didTimeout()) return
        if (request.controller.signal.aborted && !request.didTimeout()) return

        dispatch({
          type: 'search::error',
          payload: {
            append,
            message: request.didTimeout()
              ? 'Search is taking longer than expected. Please try again.'
              : 'Search is temporarily unavailable. Please try again.',
          },
        })
        if (append && pageToken) {
          requestedPageTokensRef.current.delete(pageToken)
        }
      } finally {
        if (activeRequestRef.current === request) {
          request.cleanup()
          activeRequestRef.current = null
        }
      }
    },
    [createRequest, searchFn]
  )

  const loadMore = useCallback(() => {
    if (!state.nextPageToken || state.status !== 'idle') return
    search(state.searchTerms, {
      append: true,
      pageToken: state.nextPageToken,
    })
  }, [search, state.nextPageToken, state.searchTerms, state.status])

  useEffect(() => {
    const requestedPageTokens = requestedPageTokensRef.current
    return () => {
      requestSequenceRef.current += 1
      const activeRequest = activeRequestRef.current
      activeRequest?.controller.abort()
      activeRequest?.cleanup()
      activeRequestRef.current = null
      requestedPageTokens.clear()
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      ...state,
      loading: state.status === 'loading',
      loadingMore: state.status === 'loading-more',
      loadMore,
      preloadSearchIndex,
      search,
    }),
    [state, loadMore, preloadSearchIndex, search]
  )

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  )
}

SearchProvider.propTypes = {
  searchFn: PropTypes.func.isRequired,
  preloadSearchIndex: PropTypes.func.isRequired,
  children: PropTypes.node,
}

const DiscoveryEngineProvider = ({ children }) => {
  const { access, hydrated, loading: authLoading, user } = useAuth()
  const canViewModelDetails = Boolean(
    hydrated && !authLoading && user && access?.isInstitutional === true
  )

  const searchFn = useCallback(
    async (searchTerms, signal, { pageToken = null } = {}) => {
      const trimmedSearchTerms = normalizeSearchQuery(searchTerms)
      if (!isSearchQueryEligible(trimmedSearchTerms)) {
        return createEmptySearchResponse()
      }

      const requestBody = {
        query: trimmedSearchTerms,
        ...(pageToken ? { pageToken } : {}),
      }
      const cloudRunUrl =
        process.env.GATSBY_SEARCH_ENDPOINT || DEFAULT_SEARCH_ENDPOINT

      const [response, localSearchIndex] = await Promise.all([
        fetch(cloudRunUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal,
        }),
        loadLocalSearchIndex().catch(() => createEmptyLocalSearchIndex()),
      ])

      const responseText = await response.text()
      let responseData = null

      try {
        responseData = responseText ? JSON.parse(responseText) : null
      } catch {
        responseData = null
      }

      if (!response.ok) {
        const upstreamMessage =
          responseData?.error && typeof responseData.error === 'object'
            ? responseData.error.message
            : responseData?.error
        const error = new Error(
          'Search is temporarily unavailable. Please try again.'
        )
        error.upstreamMessage =
          typeof upstreamMessage === 'string' ? upstreamMessage : ''
        throw error
      }

      if (
        !responseData ||
        typeof responseData !== 'object' ||
        Array.isArray(responseData)
      ) {
        throw new Error('Search is temporarily unavailable. Please try again.')
      }

      const matchingLocalProductResults = getProductSearchResults(
        trimmedSearchTerms,
        localSearchIndex.productIndex
      )
      const matchingLocalPortfolioResults = getPortfolioSearchResults(
        trimmedSearchTerms,
        localSearchIndex.portfolioIndex,
        canViewModelDetails
      )
      const localProductResults = pageToken ? [] : matchingLocalProductResults
      const localPortfolioResults = pageToken
        ? []
        : matchingLocalPortfolioResults
      const upstreamResults = Array.isArray(responseData.results)
        ? responseData.results
        : []
      const normalizedResults = normalizeSearchResults(
        [...localProductResults, ...localPortfolioResults, ...upstreamResults],
        {
          availablePagePaths: localSearchIndex.availablePagePaths,
          contentMetadataByPath: localSearchIndex.contentMetadataByPath,
          modelDetailPaths: localSearchIndex.modelDetailPaths,
          resolveCanonicalPath: (pathKey) =>
            getCanonicalProductPath(
              pathKey,
              localSearchIndex.availablePagePaths
            ),
        }
      )
      const catalogFilteredResults = filterCatalogSearchResults(
        normalizedResults,
        trimmedSearchTerms,
        matchingLocalProductResults
      )
      const accessibleResults = catalogFilteredResults.filter(
        (result) =>
          getSearchResultAccessState(result) !== 'institutional' ||
          canViewModelDetails
      )
      const hasAccessFilteredResults =
        accessibleResults.length !== catalogFilteredResults.length
      const hasCatalogFilteredResults =
        catalogFilteredResults.length !== normalizedResults.length
      const results = rankSearchResults(accessibleResults, trimmedSearchTerms)
      const upstreamTotalSize = Number.isFinite(responseData.totalSize)
        ? responseData.totalSize
        : upstreamResults.length

      return {
        ...createEmptySearchResponse(),
        ...responseData,
        results,
        hasAccessFilteredResults,
        hasCatalogFilteredResults,
        retrievedCount: results.length,
        totalSize:
          hasAccessFilteredResults || hasCatalogFilteredResults
            ? results.length
            : Math.max(results.length, upstreamTotalSize),
        nextPageToken:
          responseData.nextPageToken && responseData.nextPageToken !== pageToken
            ? responseData.nextPageToken
            : null,
      }
    },
    [canViewModelDetails]
  )

  return (
    <SearchProvider
      searchFn={searchFn}
      preloadSearchIndex={preloadLocalSearchIndex}
    >
      {children}
    </SearchProvider>
  )
}

DiscoveryEngineProvider.propTypes = {
  children: PropTypes.node,
}

const useSearch = () => {
  const context = useContext(SearchContext)
  if (context === undefined)
    throw new Error('useSearch must be used within a SearchProvider')
  return context
}

const SearchContextExport = {
  Context: SearchContext,
  DiscoveryEngineProvider,
  useSearch,
}

export {
  getCanonicalProductPath,
  getProductSearchResults,
  getPortfolioSearchResults,
  matchesSearchQuery,
  normalizeSearchResults,
}

export default SearchContextExport
